import { createUseCRUDService, ICRUDService } from '../../../reusableFeatures';
import { backendServices, ProfileType } from '../../../services';
import { RuleStatus } from '../../../types';
import { validateData } from '../../../ui';
import { ProfileForm } from './ProfileForm';

export function determineEntityName(profileType: ProfileType): string {
    switch (profileType) {
        case ProfileType.BuyerDestination:
            return 'Customer Profile';
        case ProfileType.BuyerWarehouse:
            return 'ASN Profile';
        case ProfileType.SupplierWarehouse:
            return 'ASN Profile';
        default:
            return 'Profile';
    }
}

export class ProfileCRUDService implements ICRUDService<number, ProfileForm> {
    private profileApi = new backendServices.Apis.ProfileApi();

    constructor(
        private readonly profileType: ProfileType,
        private readonly company: backendServices.ViewModels.CompanyViewModel,
        private readonly countryList: backendServices.ViewModels.CountryCodeViewModel[]
    ) {}

    get defaultState(): ProfileForm {
        return {
            Address1: '',
            Address2: '',
            Address3: '',
            APEMail: '',
            City: '',
            CompanyID: this.company.ID,
            Country: '',
            CompanyVatRegistrationNumber: '',
            CountryOfEstablishment: '',
            Email: '',
            Fax: '',
            InvoiceConfirmationEmail: '',
            InvoiceConfirmationEmailRepeat: '',
            LogoPath: '',
            Name: '',
            Phone: this.company.Phone,
            PostalCode: '',
            ProfileDeliveryMethods: [],
            ProfileExtendedDatas: [],
            ProfileType: this.profileType,
            Currency: 1,
            Currency_US: 'USD',
            RemittanceAdviceEmail: '',
            RemitToAddressBlock: '',
            RuleStatus: RuleStatus.Sample,
            ScanKeyProfileId: null,
            Signature: '',
            SignVATInvoices: false,
            State: '',
            VATComplianceRequired: false,
            VATNumber: '',
            VatRegistrationNumber: '',
        };
    }

    get key(): string {
        return `Profile ${this.profileType}`;
    }

    get invalidateKeys(): string[] {
        return [`ProfileSearch ${this.profileType}`];
    }

    async fetchEntity(id: number): Promise<ProfileForm> {
        const res = await this.profileApi.getProfile(id, { profileType: this.profileType });

        const profile = res.data[0];

        return { ...profile, InvoiceConfirmationEmailRepeat: profile.InvoiceConfirmationEmail };
    }

    async createEntity(): Promise<number> {
        throw new Error('Method Not Implemented yet');
    }

    private validateState(state: ProfileForm): void {
        validateData((errors) => {
            const selectedCountry = this.countryList.find((c) => c.Code === state.Country);

            const countryHasStates = selectedCountry?.StateCodes?.length ?? 0 > 0;

            if (countryHasStates && !state.State) {
                errors.push('State is required');
            }

            if (state.InvoiceConfirmationEmail !== state.InvoiceConfirmationEmailRepeat) {
                errors.push(
                    'At least one email does not match the corresponding re-entered email. Re-entered email addresses must match their corresponding email'
                );
            }

            if (!state.InvoiceConfirmationEmailRepeat?.length) {
                errors.push('ASN Confirmation Email is required');
            }

            if (!state.Name.length) {
                errors.push(`${determineEntityName(this.profileType)} Name is required`);
            }

            if (!state.Address1.length) {
                errors.push('Address Line 1 is required');
            }

            if (!state.City.length) {
                errors.push('City is required');
            }

            if (!state.PostalCode.length) {
                errors.push('ZIP / Postal Code is required');
            }

            if (!state.Country.length) {
                errors.push('Country is required');
            }
        });
    }

    async updateEntity(id: number, state: ProfileForm): Promise<void> {
        this.validateState(state);

        await this.profileApi.putProfile(id, state);
    }
}

export const useProfileCRUDService = createUseCRUDService(ProfileCRUDService);
